"use client";

import { useEffect } from "react";
import { useCartStore } from "@/store/cart.store";
import { getSocket } from "@/features/cart/socket/socket";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { cartApi } from "@/features/cart/api/cart.api";
import { Cart } from "@/types/cart.types";

export function CartSocketProvider({ children }: { children: React.ReactNode }) {
    const setCart = useCartStore((s) => s.setCart);
    const user = useAuthStore((s) => s.user);

    useEffect(() => {
        let cancelled = false;
        let handleCartUpdated: ((cart: Cart) => void) | undefined;
        let socketRef: Awaited<ReturnType<typeof getSocket>> | undefined;

        const fetchCart = async () => {
            try {
                const cart = await cartApi.getCart();
                setCart(cart);
            } catch (error) {
                console.error("Failed to fetch cart:", error);
            }
        };

        getSocket().then((socket) => {
            if (cancelled) return;
            socketRef = socket;

            // Read sessionId imperatively (not reactively) to avoid infinite loop
            const sessionId = useCartStore.getState().sessionId;

            // Build socket identity
            const newQuery: Record<string, string> = {};
            if (user?.id) {
                newQuery.userId = user.id;
            } else if (sessionId) {
                newQuery.sessionId = sessionId;
            }

            const currentQuery = (socket.io.opts.query as Record<string, string>) || {};
            const identityChanged =
                currentQuery.userId !== newQuery.userId ||
                currentQuery.sessionId !== newQuery.sessionId;

            // Reconnect with new identity if it changed
            if (identityChanged) {
                socket.io.opts.query = newQuery;
                if (socket.connected) {
                    socket.disconnect();
                }
            }

            socket.connect();
            fetchCart();

            handleCartUpdated = (cart: Cart) => {
                setCart(cart);
            };

            socket.on("cart:updated", handleCartUpdated);
        });

        return () => {
            cancelled = true;
            if (socketRef && handleCartUpdated) {
                socketRef.off("cart:updated", handleCartUpdated);
            }
        };
        // Only re-run when the authenticated user changes (login/logout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    return <>{children}</>;
}
